<template>
  <div
    class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0"
  >
    <div
      v-for="(action, actionIdx) in actions"
      :key="action.title"
      :class="[
        actionIdx === 0 ? 'rounded-t-lg sm:rounded-tr-none' : '',
        actionIdx === 1 ? 'sm:rounded-tr-lg' : '',
        actionIdx === actions.length - 2 ? 'sm:rounded-bl-lg' : '',
        actionIdx === actions.length - 1
          ? 'rounded-b-lg sm:rounded-bl-none'
          : '',
        'group relative cursor-pointer bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500',
      ]"
      @click="action.onClick"
    >
      <div>
        <span
          :class="[
            action.iconBackground,
            action.iconForeground,
            'inline-flex rounded-lg p-3 ring-4 ring-white dark:ring-transparent',
          ]"
        >
          <component :is="action.icon" class="size-6" aria-hidden="true" />
        </span>
      </div>
      <div class="mt-8">
        <h3 class="text-base font-semibold leading-6 text-gray-900">
          <nuxt-link
            v-if="action.href"
            :to="action.href"
            :target="action.target"
            class="focus:outline-none"
          >
            <!-- Extend touch target to entire panel -->
            <span class="absolute inset-0" aria-hidden="true" />
            {{ action.title }}
          </nuxt-link>
          <template v-else>
            <span class="absolute inset-0" aria-hidden="true" />
            {{ action.title }}
          </template>
        </h3>
        <p class="mt-2 text-sm text-gray-500">
          {{ action.subtitle }}
        </p>
      </div>
      <span
        class="pointer-events-none absolute right-6 top-6 text-gray-300 group-hover:text-gray-400"
        aria-hidden="true"
      >
        <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
          <path
            d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z"
          />
        </svg>
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  AcademicCapIcon,
  LinkIcon,
  QrCodeIcon,
  DocumentCheckIcon,
  ArchiveBoxIcon,
  RectangleStackIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline';
import { useUserStore } from '@/stores/user';
import { UserRole } from '@/config/user-roles';

const emit = defineEmits([
  'openTaskSlideOver',
  'openClientSlideOver',
  'openClientLinkSlideOver',
  'openClientGroupSlideOver',
  'close',
]);

const i18n = useI18n();
const { selectedRollout, getRolloutRelativeLink } = useRolloutRouting();
const userStore = useUserStore();

const actions = computed(() => {
  let rolloutSpecific;

  if (selectedRollout.value) {
    let managerSpecific;

    if (userStore.user.role >= UserRole.ROLLOUT_MANAGER) {
      managerSpecific = [
        {
          title: i18n.t('components.actions-slide-over.client-link.title'),
          subtitle: i18n.t(
            'components.actions-slide-over.client-link.subtitle'
          ),
          icon: LinkIcon,
          iconForeground: 'text-orange-700',
          iconBackground: 'bg-orange-50',
          onClick: () => {
            emit('openClientLinkSlideOver');
          },
        },
        {
          title: i18n.t('components.actions-slide-over.client-group.title'),
          subtitle: i18n.t(
            'components.actions-slide-over.client-group.subtitle'
          ),
          icon: RectangleStackIcon,
          iconForeground: 'text-purple-700',
          iconBackground: 'bg-purple-50',
          onClick: () => {
            emit('openClientGroupSlideOver');
          },
        },
      ];
    }

    rolloutSpecific = [
      {
        title: i18n.t('components.actions-slide-over.task.title'),
        subtitle: i18n.t('components.actions-slide-over.task.subtitle'),
        icon: DocumentCheckIcon,
        iconForeground: 'text-yellow-700',
        iconBackground: 'bg-yellow-50',
        onClick: () => {
          emit('openTaskSlideOver');
        },
      },
      ...(managerSpecific ?? []),
      ...(userStore.user.role >= UserRole.TECHNICIAN
        ? [
          {
            title: i18n.t('components.actions-slide-over.client.title'),
            subtitle: i18n.t('components.actions-slide-over.client.subtitle'),
            icon: ArchiveBoxIcon,
            iconForeground: 'text-stone-700',
            iconBackground: 'bg-stone-50',
            onClick: () => {
              emit('openClientSlideOver');
            },
          },
        ]
        : [
          {
            title: i18n.t(
              'components.actions-slide-over.collaborators.title'
            ),
            subtitle: i18n.t(
              'components.actions-slide-over.collaborators.subtitle'
            ),
            icon: UserGroupIcon,
            iconForeground: 'text-stone-700',
            iconBackground: 'bg-stone-50',
            href: getRolloutRelativeLink('/rollout/collaborators'),
            onClick: () => {
              emit('close');
            },
          },
        ]),
    ];
  }

  return [
    {
      title: i18n.t('components.actions-slide-over.qr-code.title'),
      subtitle: i18n.t('components.actions-slide-over.qr-code.subtitle'),
      icon: QrCodeIcon,
      iconForeground: 'text-green-700',
      iconBackground: 'bg-green-50',
    },
    ...(rolloutSpecific ?? []),
    {
      title: i18n.t('components.actions-slide-over.guides.title'),
      subtitle: i18n.t('components.actions-slide-over.guides.subtitle'),
      href: 'https://guide.rollouter.com',
      target: '_blank',
      icon: AcademicCapIcon,
      iconForeground: 'text-indigo-700',
      iconBackground: 'bg-indigo-50',
    },
  ];
});
</script>
